import { getSupabaseClient } from "./supabaseClient";
import { removeRealtimeChannel } from "./realtimeTeardown";

/**
 * Reference-counted sharing of a single Supabase broadcast channel between several
 * components.
 *
 * WHY THIS EXISTS: supabase-js returns the SAME `RealtimeChannel` object for a given
 * topic string. When two components both call `supabase.channel("agency-live-calls:X")`
 * they do not get independent subscriptions — they get one shared object, and the
 * results are subtle and bad:
 *
 *   • the second `.subscribe()` is a silent no-op (the client guards it behind
 *     `isClosed()`), so that component's status callback never fires and it runs with
 *     no health monitoring at all;
 *   • whichever component unmounts FIRST calls `removeChannel`, which tears the shared
 *     instance down — killing realtime for the component still mounted, which then
 *     silently degrades to polling and never rejoins, because its effect doesn't re-run.
 *
 * That is exactly what happened between the Agency Center "Live Calls" tab and the
 * Hierarchy tab: both derive the topic from the same viewer agency id, and both are
 * mounted at once because they live under sibling `<Tabs>` roots.
 *
 * The topic string is the SERVER's contract (the backend fans events out to
 * `agency-live-calls:{agencyId}`), so renaming one side is not an option — they must
 * genuinely share. Hence: one channel per topic, one dispatching handler per event, and
 * a set of listeners behind it. The channel is torn down only when the last listener
 * releases.
 *
 *   component A ──┐                            ┌──> listener A
 *                 ├──> one RealtimeChannel ────┤
 *   component B ──┘      (refcounted)          └──> listener B
 *
 * Note supabase-js has no `.off()`, which is why listeners live in a Set behind a single
 * registered handler rather than each registering their own `.on()`.
 */

type Listener = (payload: any) => void;

interface Entry {
  channel: any;
  /** event name → listeners for that event */
  listeners: Map<string, Set<Listener>>;
  /** Status callbacks, so every subscriber's rejoin handler still hears the truth. */
  statusListeners: Set<(status: string, err?: Error) => void>;
  refs: number;
}

const registry = new Map<string, Entry>();

export interface SharedChannelHandle {
  release: () => void;
}

/**
 * Join `topic` and receive `events`. Returns a handle whose `release()` must be called
 * from the effect's cleanup.
 *
 * `onStatus` is forwarded to every subscriber, not just the first, so each component's
 * `useRealtimeRejoin` still sees CHANNEL_ERROR / TIMED_OUT and can back off.
 */
export function acquireSharedChannel(
  topic: string,
  events: Record<string, Listener>,
  onStatus?: (status: string, err?: Error) => void,
): SharedChannelHandle {
  let released = false;
  let entry = registry.get(topic);

  const attach = (e: Entry) => {
    for (const [event, fn] of Object.entries(events)) {
      let set = e.listeners.get(event);
      if (!set) {
        set = new Set();
        e.listeners.set(event, set);
        // One registered handler per event, forever, dispatching to the live set.
        e.channel?.on("broadcast", { event }, (msg: any) => {
          for (const listener of e.listeners.get(event) ?? []) {
            try {
              listener(msg);
            } catch {
              // One misbehaving subscriber must not stop the others from being notified.
            }
          }
        });
      }
      set.add(fn);
    }
    if (onStatus) e.statusListeners.add(onStatus);
  };

  if (entry) {
    entry.refs += 1;
    attach(entry);
  } else {
    const created: Entry = {
      channel: null,
      listeners: new Map(),
      statusListeners: new Set(),
      refs: 1,
    };
    registry.set(topic, created);
    entry = created;

    void getSupabaseClient()
      .then((supabase) => {
        // Released before the client resolved — don't leave an orphan subscription.
        if (!supabase || created.refs === 0) return;
        created.channel = supabase.channel(topic);
        // Handlers requested before the channel existed still need registering.
        const pending = new Map(created.listeners);
        created.listeners = new Map();
        for (const [event, set] of pending) {
          created.listeners.set(event, set);
          created.channel.on("broadcast", { event }, (msg: any) => {
            for (const listener of created.listeners.get(event) ?? []) {
              try {
                listener(msg);
              } catch {
                /* isolated */
              }
            }
          });
        }
        created.channel.subscribe((status: string, err?: Error) => {
          for (const fn of created.statusListeners) {
            try {
              fn(status, err);
            } catch {
              /* isolated */
            }
          }
        });
      })
      .catch(() => {
        /* the caller's poll is the fallback */
      });

    attach(entry);
  }

  return {
    release: () => {
      // Effects can run cleanup more than once in StrictMode; releasing twice would
      // drive refs negative and tear the channel down under a live subscriber.
      if (released) return;
      released = true;

      const e = registry.get(topic);
      if (!e) return;

      for (const [event, fn] of Object.entries(events)) {
        e.listeners.get(event)?.delete(fn);
      }
      if (onStatus) e.statusListeners.delete(onStatus);

      e.refs -= 1;
      if (e.refs > 0) return;

      registry.delete(topic);
      removeRealtimeChannel(e.channel);
    },
  };
}
